import React, { useEffect, useRef, useState } from "react";
import {
  motion,
  useMotionTemplate,
  useMotionValue,
  useReducedMotion,
  useScroll,
  useSpring,
  useTransform,
} from "framer-motion";
import { FiArrowRight, FiArrowUpRight } from "react-icons/fi";

import { AppWrap } from "../../wrapper";
import { images } from "../../constants";
import "./Header.scss";

const metrics = [
  { value: "5+", label: "Years building UI" },
  { value: "20+", label: "Projects shipped" },
  { value: "24h", label: "Typical response" },
];

const socials = [
  { href: "https://www.linkedin.com/in/usama-sajid-dev", label: "LinkedIn" },
  { href: "https://www.instagram.com/usamasajid4/", label: "Instagram" },
];

const files = [
  { name: "App.jsx", diff: "+42 −8", state: "M" },
  { name: "Hero.jsx", diff: "+64 −12", state: "M" },
  { name: "tokens.scss", diff: "+18 −3", state: "M" },
  { name: "Work.jsx", diff: "clean", state: "✓" },
];

const codeLines = [
  { num: "01", parts: [{ t: "const", c: "kw" }, { t: " engineer", c: "id" }, { t: " = {", c: "pl" }] },
  { num: "02", parts: [{ t: "  name", c: "pr" }, { t: ": ", c: "pl" }, { t: '"Usama Sajid"', c: "str" }, { t: ",", c: "pl" }] },
  { num: "03", parts: [{ t: "  craft", c: "pr" }, { t: ": [", c: "pl" }, { t: '"interfaces"', c: "str" }, { t: ", ", c: "pl" }, { t: '"motion"', c: "str" }, { t: "],", c: "pl" }] },
  { num: "04", parts: [{ t: "  standard", c: "pr" }, { t: ": ", c: "pl" }, { t: '"production-ready"', c: "str" }, { t: ",", c: "pl" }] },
  { num: "05", parts: [{ t: "};", c: "pl" }] },
  { num: "06", parts: [{ t: "", c: "pl" }] },
  { num: "07", parts: [{ t: "export default", c: "kw" }, { t: " engineer", c: "id" }, { t: ";", c: "pl" }], caret: true },
];

const terminalLines = [
  { text: "$ npm run ship", tone: "cmd" },
  { text: "✓ types clean · 0 warnings", tone: "ok" },
  { text: "✓ bundle 48kb gzip", tone: "ok" },
  { text: "● preview ready — localhost:3000", tone: "info" },
];

const marqueeItems = [
  "React",
  "TypeScript",
  "JavaScript",
  "Sass / SCSS",
  "Redux",
  "Node.js",
  "GraphQL",
  "Framer Motion",
  "Sanity CMS",
  "Accessibility",
  "Performance",
  "Responsive Systems",
];

const HEADER_SCROLL_OFFSET = 88;
let headerScrollFrame = 0;

const easeOutCubic = (value) => 1 - Math.pow(1 - value, 3);

const scrollTo = (event, id) => {
  event.preventDefault();
  const el = document.getElementById(id);
  if (!el) return;

  cancelAnimationFrame(headerScrollFrame);

  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const targetY =
    id === "home"
      ? 0
      : Math.max(0, el.getBoundingClientRect().top + window.scrollY - HEADER_SCROLL_OFFSET);

  window.history.replaceState(null, "", window.location.pathname);

  if (reduceMotion) {
    window.scrollTo(0, targetY);
    return;
  }

  const startY = window.scrollY;
  const distance = targetY - startY;
  const duration = Math.min(900, Math.max(420, Math.abs(distance) * 0.28));
  const startTime = performance.now();

  const tick = (now) => {
    const progress = Math.min((now - startTime) / duration, 1);
    window.scrollTo(0, startY + distance * easeOutCubic(progress));

    if (progress < 1) {
      headerScrollFrame = requestAnimationFrame(tick);
      return;
    }

    window.scrollTo(0, targetY);
  };

  headerScrollFrame = requestAnimationFrame(tick);
};

const riseTransition = (delay = 0) => ({
  duration: 0.85,
  delay,
  ease: [0.22, 1, 0.36, 1],
});

const Header = () => {
  const stageRef = useRef(null);
  const stageWrapRef = useRef(null);
  const prefersReducedMotion = useReducedMotion();
  const [canTilt, setCanTilt] = useState(false);

  useEffect(() => {
    const media = window.matchMedia("(min-width: 901px) and (pointer: fine)");
    const update = () => setCanTilt(media.matches);

    update();

    if (media.addEventListener) {
      media.addEventListener("change", update);
      return () => media.removeEventListener("change", update);
    }

    media.addListener(update);
    return () => media.removeListener(update);
  }, []);

  const { scrollYProgress } = useScroll({
    target: stageRef,
    offset: ["start end", "end start"],
  });
  const parallax = useSpring(
    useTransform(scrollYProgress, [0, 1], prefersReducedMotion ? [0, 0] : [34, -34]),
    { stiffness: 120, damping: 26, mass: 0.6 }
  );

  /* mouse-tracking 3D tilt + sheen */
  const pointerX = useMotionValue(0.5);
  const pointerY = useMotionValue(0.5);
  const sheenStrength = useMotionValue(0);

  const tiltSpring = { stiffness: 140, damping: 22, mass: 0.5 };
  const rotateX = useSpring(useTransform(pointerY, [0, 1], [6.5, -6.5]), tiltSpring);
  const rotateY = useSpring(useTransform(pointerX, [0, 1], [-8.5, 8.5]), tiltSpring);
  const sheenOpacity = useSpring(sheenStrength, { stiffness: 160, damping: 28 });

  const sheenLeft = useTransform(pointerX, (v) => `${v * 100}%`);
  const sheenTop = useTransform(pointerY, (v) => `${v * 100}%`);
  const sheenBackground = useMotionTemplate`radial-gradient(460px circle at ${sheenLeft} ${sheenTop}, rgba(255, 255, 255, 0.26), transparent 62%)`;

  const handleTilt = (event) => {
    if (!canTilt || prefersReducedMotion) return;
    const bounds = stageWrapRef.current?.getBoundingClientRect();
    if (!bounds) return;

    pointerX.set((event.clientX - bounds.left) / bounds.width);
    pointerY.set((event.clientY - bounds.top) / bounds.height);
    sheenStrength.set(1);
  };

  const resetTilt = () => {
    pointerX.set(0.5);
    pointerY.set(0.5);
    sheenStrength.set(0);
  };

  /* portrait 3D tilt */
  const portraitRef = useRef(null);
  const portraitX = useMotionValue(0.5);
  const portraitY = useMotionValue(0.5);
  const portraitRotateX = useSpring(useTransform(portraitY, [0, 1], [7.5, -7.5]), tiltSpring);
  const portraitRotateY = useSpring(useTransform(portraitX, [0, 1], [-9, 9]), tiltSpring);

  const handlePortraitTilt = (event) => {
    if (!canTilt || prefersReducedMotion) return;
    const bounds = portraitRef.current?.getBoundingClientRect();
    if (!bounds) return;

    portraitX.set((event.clientX - bounds.left) / bounds.width);
    portraitY.set((event.clientY - bounds.top) / bounds.height);
  };

  const resetPortraitTilt = () => {
    portraitX.set(0.5);
    portraitY.set(0.5);
  };

  return (
    <div className="hdr">
      <motion.div
        className="hdr__masthead"
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={riseTransition(0.05)}
      >
        <span className="hdr__masthead-item">Portfolio &mdash; {new Date().getFullYear()}</span>
        <span className="hdr__masthead-status">
          <i aria-hidden="true" />
          Open for new engagements
        </span>
        <span className="hdr__masthead-item">Dubai &middot; UAE &middot; GMT+4</span>
      </motion.div>

      <div className="hdr__top">
        <div className="hdr__intro">
          <h1 className="hdr__title">
            <span className="hdr__line-mask">
              <motion.span
                className="hdr__line"
                initial={{ y: "112%", rotateX: 40 }}
                animate={{ y: "0%", rotateX: 0 }}
                style={{ transformPerspective: 620, transformOrigin: "0% 100%" }}
                transition={riseTransition(0.16)}
              >
                Crafting digital
              </motion.span>
            </span>
            <span className="hdr__line-mask">
              <motion.span
                className="hdr__line"
                initial={{ y: "112%", rotateX: 40 }}
                animate={{ y: "0%", rotateX: 0 }}
                style={{ transformPerspective: 620, transformOrigin: "0% 100%" }}
                transition={riseTransition(0.26)}
              >
                experiences with
              </motion.span>
            </span>
            <span className="hdr__line-mask">
              <motion.span
                className="hdr__line"
                initial={{ y: "112%", rotateX: 40 }}
                animate={{ y: "0%", rotateX: 0 }}
                style={{ transformPerspective: 620, transformOrigin: "0% 100%" }}
                transition={riseTransition(0.36)}
              >
                <em>rare precision.</em>
              </motion.span>
            </span>
          </h1>

          <motion.p
            className="hdr__lede"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={riseTransition(0.5)}
          >
            I turn ambitious product ideas into fast, elegant interfaces &mdash;
            designed with intent, engineered without compromise, and finished
            to the last pixel.
          </motion.p>

          <motion.div
            className="hdr__cta"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={riseTransition(0.6)}
          >
            <a href="#work" className="btn btn--dark" onClick={(e) => scrollTo(e, "work")}>
              View selected work
              <FiArrowRight />
            </a>
            <a href="#contact" className="btn btn--ghost" onClick={(e) => scrollTo(e, "contact")}>
              Start a project
            </a>
          </motion.div>

          <motion.div
            className="hdr__now"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={riseTransition(0.7)}
          >
            <span>Currently</span>
            <p>Frontend Engineer &mdash; Binghatti, Dubai</p>
          </motion.div>
        </div>

        <motion.div
          className="hdr__portrait"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={riseTransition(0.34)}
        >
          <motion.div
            className="hdr__portrait-stage"
            ref={portraitRef}
            onMouseMove={handlePortraitTilt}
            onMouseLeave={resetPortraitTilt}
            style={canTilt && !prefersReducedMotion ? { rotateX: portraitRotateX, rotateY: portraitRotateY } : undefined}
          >
            <span className="hdr__portrait-frame" aria-hidden="true" />
            <div className="hdr__portrait-media">
              <motion.img
                src={images.profile}
                alt="Usama Sajid — frontend engineer"
                initial={{ scale: 1.16, y: 14 }}
                animate={{ scale: 1, y: 0 }}
                transition={{ duration: 1.4, delay: 0.42, ease: [0.22, 1, 0.36, 1] }}
              />
            </div>
            <span className="hdr__portrait-seal" aria-hidden="true">
              <svg viewBox="0 0 100 100">
                <defs>
                  <path
                    id="hdr-seal-path"
                    d="M 50,50 m -37,0 a 37,37 0 1,1 74,0 a 37,37 0 1,1 -74,0"
                    fill="none"
                  />
                </defs>
                <text>
                  <textPath href="#hdr-seal-path">
                    Usama Sajid &middot; Portfolio &middot; Dubai UAE &middot;
                  </textPath>
                </text>
              </svg>
              <b>&#10035;</b>
            </span>
          </motion.div>
          <div className="hdr__portrait-caption">
            <em>Usama Sajid</em>
            <span>Frontend Engineer &middot; Dubai</span>
          </div>
        </motion.div>
      </div>

      <motion.div
        className="hdr__stage-wrap"
        ref={stageWrapRef}
        onMouseMove={handleTilt}
        onMouseLeave={resetTilt}
        initial={{ opacity: 0, y: 64, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={riseTransition(0.74)}
      >
        <motion.div
          className="hdr__stage3d"
          style={canTilt && !prefersReducedMotion ? { rotateX, rotateY } : undefined}
        >
          <motion.span
            className="hdr__float hdr__float--glyph"
            aria-hidden="true"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.3, duration: 0.6 }}
          >
            &lt;/&gt;
          </motion.span>
          <motion.span
            className="hdr__float hdr__float--perf"
            aria-hidden="true"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.45, duration: 0.6 }}
          >
            <b>&#9670;</b>
            60fps interactions
          </motion.span>
          <motion.span
            className="hdr__float hdr__float--checks"
            aria-hidden="true"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.6, duration: 0.6 }}
          >
            <i />
            All checks passing
          </motion.span>

          <motion.div className="hdr__stage" ref={stageRef} style={{ y: parallax }}>
          <div className="hdr__chrome">
            <span className="hdr__chrome-dots" aria-hidden="true">
              <i />
              <i />
              <i />
            </span>
            <strong>usama-sajid / portfolio &mdash; main</strong>
            <span className="hdr__chrome-user">
              <img src={images.profile} alt="Usama Sajid" />
              @usama
            </span>
          </div>

          <div className="hdr__body">
            <aside className="hdr__files" aria-hidden="true">
              <span className="hdr__files-title">Changes</span>
              {files.map((file) => (
                <span className="hdr__file" key={file.name}>
                  <em>{file.state}</em>
                  {file.name}
                  <small>{file.diff}</small>
                </span>
              ))}
            </aside>

            <div className="hdr__editor" aria-hidden="true">
              {codeLines.map((line, index) => (
                <motion.div
                  className="hdr__code-line"
                  key={line.num}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 1.0 + index * 0.08, duration: 0.4 }}
                >
                  <span className="hdr__code-num">{line.num}</span>
                  <code>
                    {line.parts.map((part, i) => (
                      <span key={i} className={`tok-${part.c}`}>
                        {part.t}
                      </span>
                    ))}
                    {line.caret && <span className="hdr__caret" />}
                  </code>
                </motion.div>
              ))}
            </div>

            <div className="hdr__terminal" aria-hidden="true">
              <span className="hdr__terminal-title">build &mdash; zsh</span>
              {terminalLines.map((line, index) => (
                <motion.span
                  className={`hdr__terminal-line hdr__terminal-line--${line.tone}`}
                  key={line.text}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1.4 + index * 0.18, duration: 0.35 }}
                >
                  {line.text}
                </motion.span>
              ))}
            </div>
          </div>

          <div className="hdr__statusbar" aria-hidden="true">
            <span className="hdr__statusbar-ok">main &#10003;</span>
            <span>prettier</span>
            <span>0 issues</span>
            <span className="hdr__statusbar-end">UTF-8 &middot; LF</span>
          </div>

          <motion.span
            className="hdr__sheen"
            aria-hidden="true"
            style={{ background: sheenBackground, opacity: sheenOpacity }}
          />
          </motion.div>
        </motion.div>
      </motion.div>

      <motion.div
        className="hdr__meta"
        initial={{ opacity: 0, y: 22 }}
        animate={{ opacity: 1, y: 0 }}
        transition={riseTransition(0.92)}
      >
        <div className="hdr__metrics">
          {metrics.map((item) => (
            <div className="hdr__metric" key={item.label}>
              <strong>{item.value}</strong>
              <span>{item.label}</span>
            </div>
          ))}
        </div>

        <div className="hdr__socials">
          {socials.map(({ href, label }) => (
            <a key={label} href={href} target="_blank" rel="noopener noreferrer">
              {label}
              <FiArrowUpRight aria-hidden="true" />
            </a>
          ))}
        </div>
      </motion.div>

      <motion.div
        className="hdr__marquee"
        aria-hidden="true"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.15, duration: 0.8 }}
      >
        <div className="hdr__marquee-track">
          {[0, 1].map((copy) => (
            <div className="hdr__marquee-group" key={copy}>
              {marqueeItems.map((item) => (
                <span key={`${copy}-${item}`}>
                  {item}
                  <i>&#9670;</i>
                </span>
              ))}
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  );
};

export default AppWrap(Header, "home", "app__band--paper");
